import { DestroyRef, inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);

  authorities$ = new BehaviorSubject<string[]>([]);
  private authorities: string[] = [];

  constructor() {
    this.authenticationService.authenticated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(authenticated => {
        let roles: string[] = [];
        if (authenticated) {
          const user = this.authenticationService.getUser();
          roles = user?.roles ? [...user.roles] : [];
          this.updateAuthorities(roles);
        } else {
          this.updateAuthorities([]);
          this.router.navigateByUrl('/unauthorized');
        }
      });
  }

  private updateAuthorities(authorities: string[]) {
    this.authorities = authorities;
    this.authorities$.next(this.getAuthorities());
  }

  getAuthorities(): string[] {
    return this.authorities.slice();
  }

  hasAuthority(authority: string): boolean {
    return this.authorities.includes(authority);
  }

  hasAny(authorities: string[]): boolean {
    return authorities.some(authority => this.hasAuthority(authority));
  }

  isTierarzt(): boolean {
    return this.hasAuthority('ISABV');
  }

  isBlv(): boolean {
    return this.hasAuthority('BLV-tamimport.ALLOW')
  }

  canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    return this.authenticationService.checkLoginState()
      .then(() => {
        if (route.data.roles) {
          return this.hasAny(route.data.roles) || this.authenticationService.generateUrlTree(route, '/forbidden');
        }
        return this.getAuthorities().length > 0 || this.authenticationService.generateUrlTree(route, '/forbidden');
      });
  }
}
